<script setup>
import _ from 'lodash'
import moment from 'moment'
import {useKeypress} from 'vue3-keypress'

// Props
const props = defineProps({
  titleSlot: {
    type: Array,
    default: null
  },
  title: {
    type: String,
    default: ''
  },
  format: {
    type: String,
    default: 'mm:ss'
  },
  keyInGroup: {
    type: [Number, String],
    default: 0
  },
  activeInGroup: {
    type: Boolean,
    default: false
  },
  color: {
    type: String,
    default: '#6d1419'
  },
  backgroundImage: {
    type: String,
    default: null
  },
  duration: {
    type: [Number, String],
    default: '00:01:00'
  },
  mode: {
    type: String,
    default: 'timer'
  }, // timer, countdown
  highlightColor: {
    type: String,
    default: 'red'
  },
  allowOverdue: {
    type: Boolean,
    default: false
  },
  highlightAt: {
    type: [String, Number],
    default: null
  },
  blinkAt: {
    type: [String, Number],
    default: null
  },
  bells: {
    type: Array,
    default: () => []
  },
  keyCode: {
    type: Number,
    default: null,
  },
  theme: {
    type: String,
    default: 'primary'
  }, // primary, lite
  layout: {
    type: String,
    default: 'vertical'
  }, // vertical, horizontal
  numberTheme: {
    type: String,
    default: 'number-primary'
  }, // number-primary, number-plain
});

// Emits
const emit = defineEmits(['start'])

// Data
const offsetStart = ref(Date.now());
const past = ref(0);
const pastFreeze = ref(0);
const active = ref(false);
const tiBlink = ref(null);
const blinkDisplay = ref(true);
const audioSrc = ref(null);

// Refs
const audio = ref(null)

const presets = {
  1: {
    src: '/timer/bell1.mp3',
  },
  2: {
    src: '/timer/bell1.mp3',
  },
}

// Computed
const timerStyle = computed(() => {
  if (props.theme === 'lite') {
    return {
      border: 'none',
      background: 'none',
    }
  }
  return {
    borderColor: props.color,
    backgroundImage: props.backgroundImage
        ? `url('${props.backgroundImage}')`
        : null,
  }
})

const durationValue = computed(() => {
  let { duration } = props
  if (props.format === 'minutes') {
    return props.duration * 60000
  }
  duration = moment(props.duration, props.format).valueOf()
  return duration - moment().startOf('day').valueOf()
})

const highlightValue = computed(() => {
  return (
      moment(props.highlightAt, props.format).valueOf() -
      moment().startOf('day').valueOf()
  )
})

const blinkValue = computed(() => {
  return (
      moment(props.blinkAt, props.format).valueOf() -
      moment().startOf('day').valueOf()
  )
})

const isOverdue = computed(() => {
  return (active.value ? past.value : pastFreeze.value) >= durationValue.value
})

const isHighlight = computed(() => {
  if (isOverdue.value) return true
  if (!props.highlightAt) return false
  if (props.mode === 'countdown') {
    if (
        (active.value ? past.value : pastFreeze.value) >=
        durationValue.value - highlightValue.value
    )
      return true
  } else {
    // eslint-disable-next-line no-lonely-if
    if ((active.value ? past.value : pastFreeze.value) >= highlightValue.value)
      return true
  }
  return false
})

const isBlink = computed(() => {
  if (!active.value) return false
  if (!props.blinkAt) return false
  if (isOverdue.value) return true
  if (props.mode === 'countdown') {
    if (
        (active.value ? past.value : pastFreeze.value) >=
        durationValue.value - blinkValue.value
    )
      return true
  } else {
    // eslint-disable-next-line no-lonely-if
    if ((active.value ? past.value : pastFreeze.value) >= blinkValue.value)
      return true
  }
  return false
})

const vBells = computed(() => {
  return _.map(props.bells, (item) => {
    const ret = {
      preset: 1,
      at: '00:30',
      overdue: false,
      ...item,
    }
    return ret
  })
})


// Methods
const loadData = async function () {
}

const formatDateTime = function (at, format) {
  if (format === 'minutes') {
    return `${_.floor(
        (moment(at).valueOf() - moment().startOf('day').valueOf()) / 60000
    )}:${moment(at).format('ss')}`
  }
  return moment(at).format(format)
}

const formatDateTimeCountDown = function (at, format) {
  const countdown = durationValue.value.valueOf() - at
  if (format === 'minutes') {
    return `${_.floor(moment(countdown).valueOf() / 60000)}:${moment(
        countdown
    ).format('ss')}`
  }
  return moment(countdown).format(format)
}

const handleStart = function () {
  active.value = true
  offsetStart.value = Date.now() - past.value
  emit('start', props.keyInGroup)
  timerFrame()
}

const timerFrame = function () {
  if (active.value) {
    const oldValue = past.value
    past.value = Date.now() - offsetStart.value
    if (past.value > durationValue.value) {
      if (!props.allowOverdue) {
        handleStop()
      }
    }
    settleAudio(oldValue, past.value)
    requestAnimationFrame(timerFrame)
  }
}

const settleAudio = function (prev, next) {
  _.forEach(vBells.value || [], (item) => {
    const atValue = momentValue(item.at, props.mode === 'countdown')
    const atValueOverdue = momentValue(item.at) + durationValue.value
    if (item.overdue && item.overdue) {
      if (prev < atValueOverdue && next >= atValueOverdue) {
        playAudio(item.preset)
      }
      return true
    }
    if (prev < atValue && next >= atValue) {
      playAudio(item.preset)
    }
    return true
  })
}

const playAudio = async function (presetId) {
  const preset = presets[presetId]
  if (!preset) return
  audioSrc.value = preset.src
  await nextTick()
  if (audio.value) {
    if (audio.value.currentTime) {
      audio.value.load()
    }
    audio.value.play()
  }
}

const handleStop = function () {
  if (!active.value) return
  past.value = Date.now() - offsetStart.value
  if (!props.allowOverdue) {
    if (past.value > durationValue.value) {
      past.value = durationValue.value
    }
  }
  pastFreeze.value = past.value
  active.value = false
}

const handleReset = function () {
  past.value = 0
  pastFreeze.value = 0
  active.value = false
}

const getOverdueStyle = function () {
  return {
    color: props.highlightColor,
  }
}

const getOverdueButtonStyle = function () {
  return {
    backgroundColor: props.highlightColor,
    color: '#fff',
  }
}

const settleTiBlink = function () {
  if (tiBlink.value) {
    clearInterval(tiBlink.value)
  }
  if (isBlink.value) {
    const isEvenSecond = Date.now() % 2000 >= 1000
    const startAfter =
        moment()
            .add(isEvenSecond ? 1 : 2, 'seconds')
            .startOf('second')
            .valueOf() - Date.now()
    if (isEvenSecond) {
      toggleBlink(false)
    } else {
      toggleBlink(true)
    }
    setTimeout(() => {
      if (isBlink.value) {
        toggleBlink(false)
        if (tiBlink.value) {
          clearInterval(tiBlink.value)
        }
        tiBlink.value = setInterval(() => {
          if (isBlink.value) {
            toggleBlink()
          }
        }, 1000)
      }
    }, startAfter)
  } else {
    toggleBlink(true)
  }
}

const toggleBlink = function (targetValue = null) {
  if (targetValue === null) {
    blinkDisplay.value = !blinkDisplay.value
  } else {
    blinkDisplay.value = targetValue
  }
}

const momentValue = function (time, isCountdown = false) {
  let ret =
      moment(time, props.format).valueOf() - moment().startOf('day').valueOf()
  if (isCountdown) {
    ret = durationValue.value - ret
  }
  return ret
}

const handleKeyPress = function (ev) {
  if (String(ev.event.key) === String(props.keyCode)) {
    if (active.value) {
      handleStop()
    } else {
      handleStart()
    }
  }
}

useKeypress({
  keyEvent: 'keyup',
  keyBinds: [],
  onAnyKey: handleKeyPress,
})


// Watch
watch(() => props.activeInGroup, function (value) {
  if (!value) {
    handleStop()
  }
})

watch(() => isBlink.value, function () {
  settleTiBlink()
})


// Mounted
onMounted(() => {
  loadData()
})

// BeforeDestroy
onBeforeUnmount(() => {
  if (tiBlink.value) {
    clearInterval(tiBlink.value)
    tiBlink.value = null
  }
})
</script>

<template lang="pug">
div(
  :style="timerStyle"
  :class="[theme, layout, numberTheme]"
).page-timer
  TimersStoryRenderer(
    v-if="titleSlot"
    :story="titleSlot"
  ).title
  div(v-else-if="title").title {{ title }}
  div(:class="{ 'is-blink-hidden': !blinkDisplay }").blink-container
    template(v-if="mode === 'countdown'")
      template(v-if="isOverdue")
        div(:style="isHighlight ? getOverdueStyle() : {}").timer-number {{ formatDateTime((active ? past : pastFreeze) - durationValue, format) }}
      template(v-else)
        div(:style="isHighlight ? getOverdueStyle() : {}").timer-number {{ formatDateTimeCountDown(active ? past : pastFreeze, format) }}
    template(v-else)
      div(:style="isHighlight ? getOverdueStyle() : {}").timer-number {{ formatDate(active ? past : pastFreeze, format) }}
  div.timer-buttons
    span(
      @click="handleStart"
      v-if="!active"
      :style="{ backgroundColor: color }"
    ).timer-button.start
      span 开始
      span.key-code {{ keyCode }}
    span(@click="handleStop" v-else :style="isOverdue ? getOverdueButtonStyle() : {}").timer-button
      span {{ isOverdue ? '已超时' : '停止' }}
      span.key-code {{ keyCode }}
    span(@click="handleReset").timer-button
      span 重置
  div(v-if="theme === 'primary'" :style="{ height: '7em' }").gap
  audio(
    :src="audioSrc"
    v-if="audioSrc"
    autoplay
    ref="audio"
  )
</template>

<style lang="scss" scoped>
@import "@/assets/scss-ui-flex";

/* latin */
@font-face {
  font-family: 'Mono';
  font-style: normal;
  font-weight: 400;
  src: url('/timer/mono4.woff2') format('woff2');
}

.page-timer {
  line-height: 1.8;
  @include ui-flex-col;
  border-radius: 2em;
  border: solid 0.02em #6d1419;
  box-sizing: border-box;
  padding: 1em 1.8em;
  background: url('/timer/timer.min.jpg') no-repeat;
  background-position: center bottom;
  background-size: cover;
  // min-width: 12em;
  height: 20em;
  color: #000;
}

.title {
  font-weight: bolder;
}

.timer-number {
  font-family: 'Mono', monospace;
  font-size: 2em;
  font-weight: normal;
  letter-spacing: -0.08em;

  &.is-overdue {
    color: red;
    background: none;
  }
}

.timer-buttons {
  @include ui-flex-row;
  line-height: 40px;
}

.timer-button {
  font-size: 14px;
  line-height: 30px;
  display: inline-block;
  padding: 0 8px;
  border-radius: 6px;
  background: #fff;
  color: #000;
  border: solid 1px #e2e2e2;
  margin: 0 5px;
  cursor: pointer;
  height: 32px;
  box-sizing: border-box;
  vertical-align: middle;
  @include ui-flex-row;

  & > span {
    flex: 0 0 auto;
  }

  &.start {
    background: #6d1419;
    color: #fff;
  }
}

.blink-container {
  transition: all 1s;

  &.is-blink-hidden {
    opacity: 0.1;
  }
}

.key-code {
  display: inline-block;
  vertical-align: middle;
  background: #fff;
  width: 2em;
  height: 2em;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  border-radius: 0.2em 0.2em 0.2em 0;
  color: #000;
  position: relative;
  left: 0.5em;
  background: url('/timer/key.min.png') no-repeat;
  background-size: 2em 2em;
  background-position: center center;
  padding-top: 0.3em;
}

.page-timer.lite {
  color: inherit;
  height: auto;
  padding: 0;

  .timer-number {
    font-size: 1em;
    line-height: 1;
  }
}

.page-timer.horizontal {
  @include ui-flex-row;

  .timer-buttons {
    margin-left: 10px;
    @include ui-flex-col(center, flex-start);

    .timer-button + .timer-button {
      margin-top: 0.5em;
    }
  }
}

.page-timer.number-primary {
  .timer-number {
    background: -webkit-linear-gradient(-45deg, #fff2d7, #f0d98d);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}
</style>
